import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { Loading, Menu, MenuItem, Text, toast } from "@foris/avocado-suite";
import Layout from "../../../../common/layout/Layout";
import { EmptyResults, Header } from "../../../../common/components";
import SectionsContainer from "../../../sections/Sections";
import { ISectionData } from "../../../sections/ISections";
import { IParams } from "../../../../models/IParams";
import useGetDimension from "@dimensions/hooks/useGetDimension";
import { Dimension } from "@models/IDimension";
import useGetPackageSection from "../hooks/useGetPackageSection";
import Tags from "@dimensions/group/GridSections/Tags/Tags";
import Modal from "@components/modal";
import usePackageUpdate from "../hooks/usePackageUpdate";
import { ContextApp, IContextApp } from "@config/Context/contextApp";
import DeletePackageModal from "../components/DeletePackageModal";
import PackageHeaderSubtitle from "../components/PackageHeaderSubtitle";
import useGetPackageSimpleGroups from "../hooks/useGetPackageSimpleGroups";
import { FiltersType } from "@modules/sections/GroupsManager/models";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import PackageTagsDrawer from "@dimensions/package/components/package-tags-drawer";
import { Events, useListenEvent } from "@common/hooks/useListenEvent";
import { useSettingsStore } from "@context/useSettingsStore";
import css from "./package.module.scss";

const isValidHost = (optionalHosts = ["tecpprd-inscripciones", "tec-inscripciones"]) => {
  if (!window?.location?.host) return false;

  const host = window?.location?.host?.split(".")?.[0] || "";
  const hostsToValidate = [...optionalHosts, "localhost", "giannina"];
  const regex = new RegExp(hostsToValidate.join("|"), "i");

  return regex.test(host);
};

const PackageCalendar: React.FC = () => {
  const { id, origin, scenario, workspace }: IParams = useParams();
  const context: IContextApp = useContext(ContextApp);
  const history = useHistory();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const contextUrl = `${workspace}/${scenario}/${origin}`;
  const { adaptedHomeUrl } = useNavigationUrl();
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [showReplicatePackageModal, setShowReplicatePackageModal] = useState(false);
  const [showReplicationError, setShowReplicationError] = useState(false);
  const [showReplicationSuccess, setShowReplicationSuccess] = useState(false);
  const [showDeletePackageModal, setShowDeletePackageModal] = useState(false);
  const [isReplicatingPackage, setIsReplicatingPackage] = useState(false);
  const [showPackageTagsDrawer, setShowPackageTagsDrawer] = useState(false);

  const userCanEditPackages = context?.user?.abilities?.can_edit_packages;
  const canPublishPackages = useSettingsStore(state => state.canPublishPackages);

  const [dimension] = useGetDimension(Dimension.package);
  const { isLoading, hasError, sections, setSections, headerData, loadPackageData, packageData } =
    useGetPackageSection({
      resourceId: id,
      scenarioId: scenario,
      originId: origin,
      workspaceId: workspace,
    });

  const { getPackageSubgroups, groups } = useGetPackageSimpleGroups({
    packageId: id,
    scenarioId: scenario,
    originId: origin,
  });

  const headerDataAdapted = useMemo(() => {
    const totalCapacityDetails = {
      title: t("dimension.package.header.more-details.total-vacancies"),
      content: "0",
    };
    const availableCapacityDetails = {
      title: t("dimension.package.header.more-details.available-vacancies"),
      content: "0",
    };

    if (groups?.length && packageData?.isPublished) {
      const packageTypeGroups = groups?.filter(({ referentType }) => referentType === "PACKAGE");
      const capacityList = packageTypeGroups?.map(group => group?.capacity);
      const availableCapacityList = packageTypeGroups?.map(
        group => (group?.capacity ?? 0) - (group?.enrollmentStats?.usedCapacity ?? 0),
      );

      const totalCapacity = Math.min(...capacityList);
      const availableCapacity = Math.min(...availableCapacityList);

      totalCapacityDetails.content = totalCapacity.toString();
      availableCapacityDetails.content = availableCapacity.toString();
    }

    return {
      ...headerData,
      moreDetails: packageData?.isPublished
        ? [totalCapacityDetails, availableCapacityDetails]
        : headerData?.moreDetails,
    };
  }, [headerData, groups, language]);

  useEffect(() => {
    if (packageData?.isPublished) {
      getPackageSubgroups();
    }
  }, [packageData?.isPublished]);

  const {
    data: { isLoading: isUpdatingPackage, response },
    handleUpdatePackage,
    handleDeletePackage,
  } = usePackageUpdate({
    originId: origin,
    scenarioId: scenario,
    onSuccess: loadPackageData,
    onErrorDeleting: () =>
      toast.error(t("dimension.package.notifications.delete-package-error"), {
        autoClose: 5000,
      }),
    onDeleted() {
      history.replace(`/home/${workspace}/${scenario}/${origin}`);
    },
  });

  const setDataCalendar = (data: ISectionData, callback: () => void) => {
    setSections(data);
    callback && callback();
  };

  useEffect(() => {
    if (isReplicatingPackage && !isUpdatingPackage && !isLoading) {
      setIsReplicatingPackage(false);

      if (!response?.commited || !response?.payload?.updates[0]?.replicatedPackages?.length) {
        setShowReplicationError(true);
      }

      if (response?.commited && response?.payload?.updates[0]?.replicatedPackages?.length) {
        setShowReplicationSuccess(true);
      }
    }
  }, [isUpdatingPackage, isLoading]);

  useEffect(() => {
    if (!sections && !hasError) loadPackageData();
  }, [sections, hasError]);

  useListenEvent({
    eventKey: "on:refresh",
    handler: (detail: Events["on:refresh"]) => {
      if (detail?.target === "package-calendar") {
        loadPackageData();
      }
    },
  });

  const handleGroupsManagerClick = () => {
    history.push(`/editor/groups-manager/${contextUrl}`, {
      filter: FiltersType.PACKAGE,
      data: packageData,
    });
  };

  const getMenuOptions = (): MenuItem[] => {
    const menuOptions = [];
    const hasClashLabel = packageData?.labels?.some(label =>
      ["CLSHP", "package_clash"].includes(label?.code),
    );

    if (context?.user?.permissions?.update && context?.user?.abilities?.can_edit_assignment) {
      menuOptions.push({
        label: t("dimension.package.header.menu.options.link-management"),
        icon: "edit",
        onClick: () =>
          history.push(
            `/scheduler/editor/package/links-management/${workspace}/${scenario}/${origin}/${id}`,
          ),
      });
    }

    if (isValidHost(["tecpprd-inscripciones", "tec-inscripciones", "grupouax-qa", "grupouax"])) {
      if (!packageData?.isPublished && !hasClashLabel && canPublishPackages) {
        menuOptions.push({
          label: t("dimension.package.header.menu.options.publish-package"),
          icon: "arrow-up",
          onClick: () => setShowPackageModal(true),
        });
      }

      if (packageData?.isPublished && !hasClashLabel) {
        menuOptions.push({
          label: t("dimension.package.header.menu.options.change-visibility"),
          icon: "open-eye",
          onClick: () =>
            handleUpdatePackage({
              packageId: packageData?.id,
              isVisible: !packageData?.isVisible,
            }),
        });
      }
    }

    if (packageData?.isReplicated === null && !packageData?.isPublished && isValidHost()) {
      menuOptions.push({
        label: t("dimension.package.header.menu.options.replicate-package"),
        icon: "copy",
        onClick: () => setShowReplicatePackageModal(true),
      });
    }

    if (context?.user?.abilities?.can_delete_packages) {
      menuOptions.push({
        label: t("dimension.package.header.menu.options.delete-package"),
        icon: "trash",
        disabled: packageData?.isPublished,
        tooltipLabel: packageData?.isPublished
          ? t("dimension.package.header.menu.options.publish-package-tooltip")
          : "",
        onClick: packageData?.isPublished ? () => null : () => setShowDeletePackageModal(true),
      });
    }

    if (context?.user?.abilities?.can_access_group_manager) {
      menuOptions.push({
        label: t("dimension.package.header.menu.options.group-manager"),
        onClick: () => handleGroupsManagerClick(),
      });
    }

    return menuOptions;
  };

  const handlePublishPackage = () => {
    setShowPackageModal(false);

    handleUpdatePackage({
      packageId: packageData?.id,
      isPublished: true,
    });
  };

  const handleReplicatePackage = () => {
    setShowReplicatePackageModal(false);
    setIsReplicatingPackage(true);

    handleUpdatePackage({
      packageId: packageData?.id,
      replicated: true,
    });
  };

  const handleCloseReplicationSnackbar = () => {
    setShowReplicationError(false);
    setShowReplicationSuccess(false);
  };

  const handleDeletePackageRequest = () => {
    setShowDeletePackageModal(false);
    handleDeletePackage({ packageId: packageData?.id });
  };

  const showSkeleton = (isLoading || !headerDataAdapted || !packageData || !sections) && !hasError;

  useEffect(() => {
    if (showReplicationError || showReplicationSuccess) {
      handleCloseReplicationSnackbar();

      const toastType = showReplicationError ? "error" : "success";
      toast[toastType](
        showReplicationError
          ? t("dimension.package.notifications.replication-error")
          : t("dimension.package.notifications.replication-success", {
              count: response?.payload?.updates[0]?.replicatedPackages?.length,
            }),
        {
          autoClose: 8000,
        },
      );
    }
  }, [showReplicationError, showReplicationSuccess]);

  return (
    <Layout contextSearch>
      {!hasError && (
        <Header
          isLoading={
            showSkeleton
              ? {
                  subTitle: true,
                  title: true,
                  topTitle: true,
                  moreDetails: true,
                }
              : undefined
          }
          dimension={dimension.label}
          {...(headerDataAdapted ?? {})}
          renderMenu={() =>
            userCanEditPackages && !!getMenuOptions()?.length ? (
              <Menu items={getMenuOptions()} listOrigin="right" />
            ) : null
          }
          subTitle={
            <PackageHeaderSubtitle
              headerDataAdapted={headerDataAdapted}
              packageData={packageData}
            />
          }
          breadcrumbItems={[
            {
              path: adaptedHomeUrl(),
              title: t("dimension.package.breadcrumb.previous"),
            },
            {
              title: t("dimension.package.breadcrumb.current"),
            },
          ]}
        >
          <div className={css.labelsWrapper}>
            <Tags
              sectionTitle={t("header.tag-manual.title")}
              labels={headerDataAdapted?.labels}
              addMargin={false}
              onEdit={() => setShowPackageTagsDrawer(true)}
            />
          </div>
        </Header>
      )}

      {isUpdatingPackage && <Loading />}

      {hasError && <EmptyResults dimension={t("dimension.package.title")} isCalendarView />}

      {!hasError && (
        <SectionsContainer
          isLoading={showSkeleton}
          config={{
            scenarioId: parseInt(scenario),
            originId: parseInt(origin),
            workspaceId: parseInt(workspace),
          }}
          defaultTerm={packageData?.population?.term}
          data={sections}
          setDataGrid={setDataCalendar}
        />
      )}

      <Modal
        typeState="confirm"
        show={showReplicatePackageModal}
        title={t("dimension.package.modals.replicate-package.title")}
        subtitle={t("dimension.package.modals.general-warning-subtitle")}
        onClose={() => setShowReplicatePackageModal(false)}
        textButtonPrincipal={t("dimension.package.modals.replicate-package.btn-replicate")}
        textButtonSecondary={t("dimension.package.modals.replicate-package.btn-cancel")}
        onClickPrincipal={handleReplicatePackage}
        onClickSecondary={() => setShowReplicatePackageModal(false)}
      >
        <Text>{t("dimension.package.modals.replicate-package.description")}</Text>
      </Modal>

      <Modal
        typeState="confirm"
        show={showPackageModal}
        title={t("dimension.package.modals.publish-package.title")}
        subtitle={t("dimension.package.modals.general-warning-subtitle")}
        onClose={() => setShowPackageModal(false)}
        textButtonPrincipal={t("dimension.package.modals.publish-package.btn-publish")}
        textButtonSecondary={t("dimension.package.modals.publish-package.btn-cancel")}
        onClickPrincipal={handlePublishPackage}
        onClickSecondary={() => setShowPackageModal(false)}
      >
        <Text>{t("dimension.package.modals.publish-package.description")}</Text>
      </Modal>

      <DeletePackageModal
        isOpen={showDeletePackageModal}
        onConfirm={handleDeletePackageRequest}
        onCancel={() => setShowDeletePackageModal(false)}
      />

      {!!packageData && !showSkeleton && (
        <PackageTagsDrawer
          isOpen={showPackageTagsDrawer}
          labels={packageData?.labels}
          onClose={() => setShowPackageTagsDrawer(false)}
        />
      )}
    </Layout>
  );
};

export default PackageCalendar;
